import { Component, forwardRef, inject, ChangeDetectionStrategy } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { CheckboxComponent } from "@components/checkbox/checkbox.component";
import { DatepickerIntervalComponent } from "@components/datepicker-interval/datepicker-interval.component";
import { DatepickerComponent } from "@components/datepicker/datepicker.component";
import { SelectorComponent } from "@components/selector/selector.component";
import remove from "lodash/remove";
import { DateUtils } from "~app/common/utils/date-utils";
import { ArbetsorderSearchHybridService } from "~app/features/planera/arbetsorder-search/arbetsorder-search-hybrid.service";
import { ArbetsorderSearchFilterComponent } from "../../components/arbetsorder-search-filter/arbetsorder-search-filter.component";
import { ArbetsorderSearchBasePanel } from "../../models/arbetsorder-search-base-panel";

@Component({
    selector: "rask-arbetsorder-search-tid",
    templateUrl: "./arbetsorder-search-tid.component.html",
    styleUrls: ["./arbetsorder-search-tid.component.scss"],
    providers: [
        {
            provide: ArbetsorderSearchBasePanel,
            useExisting: forwardRef(() => ArbetsorderSearchTidComponent)
        }
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        ArbetsorderSearchFilterComponent,
        SelectorComponent,
        FormsModule,
        DatepickerIntervalComponent,
        DatepickerComponent,
        CheckboxComponent
    ]
})
export class ArbetsorderSearchTidComponent extends ArbetsorderSearchBasePanel {
    skordningSkotningValues = [
        "SKOTNING",
        "SKÖRDNING"
    ];

    private readonly hybridService = inject(ArbetsorderSearchHybridService);

    get arbetsorderCreatedDateFrom() {
        if (this.searchFilter.arbetsorderCreatedDateFrom) {
            return this.searchFilter.arbetsorderCreatedDateFrom;
        } else {
            return null;
        }
    }

    get arbetsorderCreatedDateTom() {
        if (this.searchFilter.arbetsorderCreatedDateTom) {
            return this.searchFilter.arbetsorderCreatedDateTom;
        } else {
            return null;
        }
    }

    get faltplaneratDatum() {
        if (this.searchFilter.faltplaneratDatum) {
            return this.searchFilter.faltplaneratDatum;
        } else {
            return null;
        }
    }

    set faltplaneratDatum(date: string) {
        if (date) {
            this.searchFilter.faltplaneratDatum = DateUtils.endOfDayDateTime(date);
        } else {
            this.searchFilter.faltplaneratDatum = null;
        }
    }

    get periodFromDate() {
        if (this.searchFilter.periodDateFrom) {
            return this.searchFilter.periodDateFrom;
        } else {
            return null;
        }
    }

    get periodTomDate() {
        if (this.searchFilter.periodDateTom) {
            return this.searchFilter.periodDateTom;
        } else {
            return null;
        }
    }

    get periodsattPeriod() {
        if (this.searchFilter.periodsattPeriod) {
            return this.searchFilter.periodsattPeriod;
        } else {
            return null;
        }
    }

    set periodsattPeriod(date: string) {
        if (date) {
            this.ejTidsattaDefaultValue();
            this.searchFilter.periodsattPeriod = date;
        } else {
            this.searchFilter.periodsattPeriod = null;
        }
    }

    get searchFilterEjTidsatta(): boolean {
        return this.searchFilter.ejTidsatta;
    }

    set searchFilterEjTidsatta(value: boolean) {
        this.searchFilter.ejTidsatta = value;
        if (value) {
            this.ejTidsattaUnsetsPeriodAndMaskinlag();
        }
    }

    get skordningSkotning() {
        if (this.searchFilter.skordningSkotning) {
            return this.searchFilter.skordningSkotning;
        } else {
            return null;
        }
    }

    set skordningSkotning(ss: string) {
        this.searchFilter.skordningSkotning = ss;
    }

    get virkesorderAndradFrom() {
        if (this.searchFilter.virkesorderAndradFrom) {
            return this.searchFilter.virkesorderAndradFrom;
        } else {
            return null;
        }
    }

    get virkesorderAndradTom() {
        if (this.searchFilter.virkesorderAndradTom) {
            return this.searchFilter.virkesorderAndradTom;
        } else {
            return null;
        }
    }

    get virkesorderGiltigFrom() {
        if (this.searchFilter.virkesorderGiltigFrom) {
            return this.searchFilter.virkesorderGiltigFrom;
        } else {
            return null;
        }
    }

    get virkesorderGiltigTom() {
        if (this.searchFilter.virkesorderGiltigTom) {
            return this.searchFilter.virkesorderGiltigTom;
        } else {
            return null;
        }
    }

    get virkesorderSkapadFrom() {
        if (this.searchFilter.virkesorderSkapadFrom) {
            return this.searchFilter.virkesorderSkapadFrom;
        } else {
            return null;
        }
    }

    get virkesorderSkapadTom() {
        if (this.searchFilter.virkesorderSkapadTom) {
            return this.searchFilter.virkesorderSkapadTom;
        } else {
            return null;
        }
    }

    forceUpdateFilterChange(): void {
        // Krav från abstrakt klass
    }

    isDirty(): boolean {
        return (
            (this.searchFilter.ejTidsatta ||
                this.searchFilter.periodDateFrom !== null ||
                this.searchFilter.periodDateTom !== null) ||
            this.searchFilter.arbetsorderCreatedDateFrom !== null ||
            this.searchFilter.arbetsorderCreatedDateTom !== null ||
            this.searchFilter.virkesorderSkapadFrom !== null ||
            this.searchFilter.virkesorderSkapadTom !== null ||
            this.searchFilter.virkesorderGiltigFrom !== null ||
            this.searchFilter.virkesorderGiltigTom !== null ||
            this.searchFilter.virkesorderAndradFrom !== null ||
            this.searchFilter.virkesorderAndradTom !== null ||
            this.searchFilter.faltplaneratDatum !== null
        );
    }

    setArbetsorderCreatedDateFrom(date: any) {
        if (date) {
            this.searchFilter.arbetsorderCreatedDateFrom = date;
        } else {
            this.searchFilter.arbetsorderCreatedDateFrom = null;
        }
    }

    setArbetsorderCreatedDateTom(date: any) {
        if (date) {
            this.searchFilter.arbetsorderCreatedDateTom = DateUtils.endOfDayDateTime(date);
        } else {
            this.searchFilter.arbetsorderCreatedDateTom = null;
        }
    }

    setPeriodFromDate(date: any) {
        if (date) {
            this.ejTidsattaDefaultValue();
            this.searchFilter.periodDateFrom = date;
        } else {
            this.searchFilter.periodDateFrom = null;
        }
    }

    setPeriodTomDate(date: any) {
        if (date) {
            this.ejTidsattaDefaultValue();
            this.searchFilter.periodDateTom = DateUtils.endOfDayDateTime(date);
        } else {
            this.searchFilter.periodDateTom = null;
        }
    }

    setVirkesorderAndradDateFrom(date: any) {
        if (date) {
            this.searchFilter.virkesorderAndradFrom = date;
        } else {
            this.searchFilter.virkesorderAndradFrom = null;
        }
    }

    setVirkesorderAndradDateTom(date: any) {
        if (date) {
            this.searchFilter.virkesorderAndradTom = DateUtils.endOfDayDateTime(date);
        } else {
            this.searchFilter.virkesorderAndradTom = null;
        }
    }

    setVirkesorderCreatedDateFrom(date: any) {
        if (date) {
            this.searchFilter.virkesorderSkapadFrom = date;
        } else {
            this.searchFilter.virkesorderSkapadFrom = null;
        }
    }

    setVirkesorderCreatedDateTom(date: any) {
        if (date) {
            this.searchFilter.virkesorderSkapadTom = DateUtils.endOfDayDateTime(date);
        } else {
            this.searchFilter.virkesorderSkapadTom = null;
        }
    }

    setVirkesorderGiltigDateFrom(date: any) {
        if (date) {
            this.searchFilter.virkesorderGiltigFrom = date;
        } else {
            this.searchFilter.virkesorderGiltigFrom = null;
        }
    }

    setVirkesorderGiltigDateTom(date: any) {
        if (date) {
            this.searchFilter.virkesorderGiltigTom = DateUtils.endOfDayDateTime(date);
        } else {
            this.searchFilter.virkesorderGiltigTom = null;
        }
    }

    private ejTidsattaDefaultValue() {
        this.searchFilter.ejTidsatta = false;
    }

    private ejTidsattaUnsetsPeriodAndMaskinlag() {
        this.setPeriodFromDate(null);
        this.setPeriodTomDate(null);
        this.periodsattPeriod = null;
        remove(this.searchFilter.status.produktionsstatus.maskinlag, m => m === true);
        this.hybridService.triggerSetMinaMaskinlag();
    }
}
